import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { tgReady } from './telegram';
import { subscribeUserSettings, getUserSettings } from './userSettings';
import { bootAuth, api } from './api';
import { installErrorReporter } from './errorReporter';
// Self-hosted fonts — drops the Google Fonts DNS round-trip on cold-start
// (matters most for slow-network Telegram WebApp users). RU-only ship →
// import the cyrillic subset only (each non-cyrillic subset would add
// ~3-4 KB gz of dead @font-face declarations for glyph ranges we never use).
import '@fontsource/russo-one/cyrillic-400.css';
import '@fontsource/manrope/cyrillic-400.css';
import '@fontsource/manrope/cyrillic-500.css';
import '@fontsource/manrope/cyrillic-600.css';
import '@fontsource/manrope/cyrillic-700.css';
import '@fontsource/manrope/cyrillic-800.css';
import './styles.css';

installErrorReporter(api);

// Defensive — if not running inside Telegram, this is a no-op.
tgReady();

document.addEventListener('contextmenu', (e) => e.preventDefault());
document.addEventListener('selectstart', (e) => e.preventDefault());

function applyReducedMotion(reduce: boolean): void {
  if (typeof document === 'undefined') return;
  document.body.classList.toggle('reduced-motion', reduce);
}

applyReducedMotion(getUserSettings().reducedMotion);
subscribeUserSettings((s) => applyReducedMotion(s.reducedMotion));

function applyTheme(theme: 'cyber' | 'neon' | 'mil'): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

applyTheme(getUserSettings().theme);
subscribeUserSettings((s) => applyTheme(s.theme));

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element missing from index.html');

// Render IMMEDIATELY — never gate first paint on the network. bootAuth() runs a
// chain of auth + hydration fetches that can hang indefinitely in a flaky
// Telegram WebView (the api client has no socket-level timeout). Awaiting it
// here used to leave the app stuck on a blank #root — i.e. Telegram's native
// "CAT VS AI" loading card — forever, every launch, with no recovery. The app
// is fully usable from the localStorage cache and hydrates reactively as
// bootAuth() resolves (or fails) in the background.
createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

void bootAuth();
